import { DeviceAccessorDef } from "./DeviceAccessor";

interface Capacitor {
    Plugins?: CapacitorPlugins;
    isNativePlatform: () => boolean;
    getPlatform: () => string;
}

interface CapacitorPlugins {
    Device?: {
        getInfo: () => Promise<MobileDeviceInfo>;
    },
    StatusBar?: MobileDeviceStatusBar;
}

interface MobileDeviceInfo {
    model: string;
    platform: string;
    operatingSystem: string;
    osVersion: string;
    manufacturer: string;
    isVirtual: boolean;
}

interface MobileDeviceStatusBar {
    show: () => Promise<void>;
    hide: () => Promise<void>;
    setStyle: (options: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }) => Promise<void>;
    setBackgroundColor: (options: { color: string }) => Promise<void>;
    setOverlaysWebView: (options: { overlay: boolean }) => Promise<void>;
}

export class MobileDeviceAccessorDef implements DeviceAccessorDef {
    private _deviceInfo: MobileDeviceInfo;
    private _statusBar: MobileDeviceStatusBar;

    private static get capacitorInstance(): Capacitor {
        return window["Capacitor"] ?? window.parent["Capacitor"];
    }

    private static get capacitorPlugins(): CapacitorPlugins {
        return this.capacitorInstance["Plugins"];
    }

    public static get hasCapacitor(): boolean {
        return this.capacitorInstance !== undefined;
    }

    private async getDeviceInfo(): Promise<MobileDeviceInfo> {
        if (this._deviceInfo == null) {
            await MobileDeviceAccessorDef.capacitorPlugins.Device.getInfo()
                .then((info: MobileDeviceInfo) => { this._deviceInfo = info; });
        }
        return this._deviceInfo;
    }

    private get statusBar(): MobileDeviceStatusBar {
        if (this._statusBar == null) {
            this._statusBar = MobileDeviceAccessorDef.capacitorPlugins.StatusBar;
        }
        return this._statusBar;
    }

    get browser(): string {
        return "McLeodConnect";
    }

    get platform(): string {
        return MobileDeviceAccessorDef.capacitorInstance?.getPlatform();
    }

    get accessOrigin(): 'web' | 'app' {
        if (MobileDeviceAccessorDef.capacitorInstance?.isNativePlatform() ?? false)
            return 'app';
        else
            return 'web';
    }

    async isVirtual(): Promise<boolean> {
        const info = await this.getDeviceInfo();
        return info.isVirtual;
    }

    statusBarStyle(style: 'DARK' | 'LIGHT' | 'DEFAULT'): void {
        this.statusBar?.setStyle({ style: style });
    }

    statusBarColor(color: string): void {
        this.statusBar?.setBackgroundColor({ color: color });
    }

    statusBarOverlaysWebView(doesOverlay: boolean): void {
        this.statusBar?.setOverlaysWebView({ overlay: doesOverlay });
    }

    statusBarVisibility(visible: boolean): void {
        visible ? this.statusBar?.show() : this.statusBar?.hide();
    }
}
